import { UNKNOWN_ORGANISATION } from '@/api/model/Organisation';
import { defineQueryOptions, useQuery, useQueryCache } from '@pinia/colada';
import * as Sentry from '@sentry/vue';
import { watch } from 'vue';

import LaravelError from '@/api/model/LaravelError';
import type { BadgeVariant } from '@/data/menu';
import { apiFetch } from '@/lib/http';
import { useMenuStore } from '@/stores/menuStore';
import { useOrganisationStore } from '@/stores/organisationStore';

export interface CountsResponse {
    disputes: number;
    transfers: {
        drafts: number;
        problems: number;
    };
    variants: number;
    orders: number;
    purchase_orders_unreconciled: number;
}

const EMPTY_COUNTS: CountsResponse = {
    disputes: 0,
    transfers: { drafts: 0, problems: 0 },
    variants: 0,
    orders: 0,
    purchase_orders_unreconciled: 0,
};

const COUNTS_QUERY_KEYS = {
    root: ['counts'] as const,
    list: (orgPublicId: string) => [...COUNTS_QUERY_KEYS.root, orgPublicId] as const,
};

const countsQuery = defineQueryOptions(({ orgPublicId }: { orgPublicId: string }) => ({
    key: COUNTS_QUERY_KEYS.list(orgPublicId),
    query: async (): Promise<CountsResponse> => {
        try {
            return await apiFetch<CountsResponse>(`/api/organisations/${orgPublicId}/counts`);
        } catch (error) {
            const status = error instanceof LaravelError ? error.localCause?.response?.status : undefined;
            if (status !== 401 && status !== 419) {
                Sentry.captureException(error, {
                    tags: { category: 'counts', action: 'fetch' },
                    extra: { organisation: orgPublicId },
                });
            }
            return EMPTY_COUNTS;
        }
    },
    enabled: !!orgPublicId && orgPublicId !== UNKNOWN_ORGANISATION,
}));

function useCountsQuery() {
    const organisationStore = useOrganisationStore();
    return useQuery(() =>
        countsQuery({
            orgPublicId: organisationStore.current_organisation.public_id,
        }),
    );
}

export function useInvalidateCounts() {
    const queryCache = useQueryCache();
    return () => {
        void queryCache.invalidateQueries({ key: COUNTS_QUERY_KEYS.root });
    };
}

/**
 * Wires up the global menu-badge updates: runs the counts query and pushes
 * each value into menuStore whenever the response changes. Mount this once
 * (in App.vue) — calling it elsewhere would create duplicate watchers.
 */
export function useMenuBadgesFromCounts() {
    const counts = useCountsQuery();
    const menuStore = useMenuStore();

    function updateBadge(label: string, count: number, variant: BadgeVariant = 'danger') {
        if (count === 0) {
            menuStore.removeBadge(label);
        } else {
            menuStore.addBadge(label, count, variant);
        }
    }

    watch(
        () => counts.data.value,
        (data) => {
            if (!data) return;
            updateBadge('Disputes', data.disputes ?? 0);
            updateBadge('Upcoming Transfers', data.transfers?.drafts ?? 0, 'warning');
            // Problems badge sits on the top-level "Warehouse Transfers" item;
            // clicking it routes to the problems-filtered list via `badge-to`.
            updateBadge('Warehouse Transfers', data.transfers?.problems ?? 0);
            updateBadge('Variants', data.variants ?? 0);
            updateBadge('Orders', data.orders ?? 0);
            updateBadge('Purchase Orders', data.purchase_orders_unreconciled ?? 0, 'warning');
        },
        { immediate: true },
    );
}
